import { useEffect, useState } from "react";
import { useCloudStore, type CloudResource } from "@/stores/cloudStore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Zap, HardDrive, Database, Container, Globe, Table,
  ArrowRightLeft, Cloud, Plus, Search, RefreshCw, Trash2,
  Loader2, Server, Monitor,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { CostSummaryCard } from "./CostSummaryCard";
import { AddResourceModal } from "./AddResourceModal";

interface CloudTabProps {
  projectId: number;
  projectName: string;
}

const RESOURCE_TYPE_CONFIG: Record<string, { icon: typeof Zap; color: string; label: string }> = {
  lambda: { icon: Zap, color: "#F79400", label: "Lambda" },
  s3: { icon: HardDrive, color: "#3F8624", label: "S3" },
  rds: { icon: Database, color: "#3B48CC", label: "RDS" },
  ecs: { icon: Container, color: "#ED7100", label: "ECS" },
  apigateway: { icon: Globe, color: "#A166FF", label: "API Gateway" },
  dynamodb: { icon: Table, color: "#4053D6", label: "DynamoDB" },
  sqs: { icon: ArrowRightLeft, color: "#FF9900", label: "SQS" },
  cloudfront: { icon: Cloud, color: "#8C4FFF", label: "CloudFront" },
  ec2: { icon: Server, color: "#FF9900", label: "EC2" },
  elasticache: { icon: Database, color: "#C925D1", label: "ElastiCache" },
  sns: { icon: ArrowRightLeft, color: "#D6336C", label: "SNS" },
};

function getResourceConfig(type: string) {
  return RESOURCE_TYPE_CONFIG[type] || { icon: Monitor, color: "#6b7280", label: type };
}

export function CloudTab({ projectId, projectName }: CloudTabProps) {
  const {
    resources,
    costs,
    loading,
    discovering,
    refreshingCosts,
    fetchResources,
    addResource,
    removeResource,
    discoverResources,
    fetchCosts,
    refreshCosts,
  } = useCloudStore();
  const { toast } = useToast();
  const [addModalOpen, setAddModalOpen] = useState(false);

  const projectResources = resources[projectId] || [];
  const projectCosts = costs[projectId];
  const isLoading = loading[projectId] || false;
  const isDiscovering = discovering[projectId] || false;
  const isRefreshingCosts = refreshingCosts[projectId] || false;

  useEffect(() => {
    fetchResources(projectId);
    fetchCosts(projectId);
  }, [projectId]);

  const handleDiscover = async () => {
    try {
      const result = await discoverResources(projectId);
      toast({
        title: "Discovery complete",
        description: `Found ${result.discovered} resources. ${result.added} new, ${result.skipped} already tracked.`,
      });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Discovery failed",
        description: e instanceof Error ? e.message : "Failed to discover resources",
      });
    }
  };

  const handleRefreshCosts = async () => {
    try {
      await refreshCosts(projectId);
      toast({
        title: "Costs refreshed",
        description: "Cost data has been updated from AWS Cost Explorer.",
      });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Cost refresh failed",
        description: e instanceof Error ? e.message : "Failed to refresh costs",
      });
    }
  };

  const handleAddResource = async (data: {
    resource_arn: string;
    resource_type: string;
    resource_name: string;
    region: string;
  }) => {
    try {
      await addResource(projectId, data);
      toast({ title: "Resource added", description: `${data.resource_name} has been added.` });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Failed to add resource",
        description: e instanceof Error ? e.message : "Unknown error",
      });
      throw e;
    }
  };

  const handleRemoveResource = async (resource: CloudResource) => {
    try {
      await removeResource(resource.id, projectId);
      toast({ title: "Resource removed", description: `${resource.resourceName} has been removed.` });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Failed to remove resource",
        description: e instanceof Error ? e.message : "Unknown error",
      });
    }
  };

  return (
    <div className="space-y-4 p-1">
      {/* Actions bar */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">
          AWS Resources for {projectName}
        </h3>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleDiscover}
            disabled={isDiscovering}
            className="gap-2"
          >
            {isDiscovering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Discover
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleRefreshCosts}
            disabled={isRefreshingCosts}
            className="gap-2"
          >
            {isRefreshingCosts ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh Costs
          </Button>
          <Button
            size="sm"
            onClick={() => setAddModalOpen(true)}
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Resource
          </Button>
        </div>
      </div>

      {/* Cost Summary */}
      <CostSummaryCard costs={projectCosts} />

      {/* Resources List */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Cloud className="w-4 h-4" />
            Resources ({projectResources.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : projectResources.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No cloud resources linked. Use "Discover" to find tagged AWS resources or "Add Resource" to add manually.
            </p>
          ) : (
            <ScrollArea className="max-h-[400px]">
              <div className="space-y-2">
                {projectResources.map((resource) => {
                  const config = getResourceConfig(resource.resourceType);
                  const Icon = config.icon;
                  return (
                    <div
                      key={resource.id}
                      className="flex items-center gap-3 p-3 rounded-md border bg-card hover:bg-muted/50 transition-colors group"
                    >
                      <div
                        className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${config.color}20` }}
                      >
                        <Icon className="w-4 h-4" style={{ color: config.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">
                            {resource.resourceName}
                          </span>
                          <Badge variant="outline" className="text-xs flex-shrink-0">
                            {config.label}
                          </Badge>
                          {resource.discoveryMethod === "auto" && (
                            <Badge variant="secondary" className="text-xs flex-shrink-0">
                              auto
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {resource.region} &middot; {resource.resourceArn}
                        </p>
                      </div>
                      {resource.costMonthly > 0 && (
                        <span className="text-sm font-medium text-muted-foreground flex-shrink-0">
                          ${resource.costMonthly.toFixed(2)}/mo
                        </span>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        onClick={() => handleRemoveResource(resource)}
                        aria-label="Remove resource"
                      >
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      <AddResourceModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={handleAddResource}
      />
    </div>
  );
}
